import * as THREE from 'three';
import { Loop } from '../core/Loop';
import { createRenderer, resizeRenderer } from '../core/Renderer';
import { SteerInput, type InputAction } from '../core/SteerInput';
import { Crumbs } from '../entities/Crumbs';
import { Duck } from '../entities/Duck';
import { Hazards } from '../entities/Hazards';
import { ITEM_ICON, ITEM_NAME, PowerUps } from '../entities/PowerUps';
import { RIVAL_PROFILES, Rival } from '../entities/Rival';
import { RiverView } from '../entities/RiverView';
import { AudioSystem } from '../systems/AudioSystem';
import { CameraRig } from '../systems/CameraRig';
import { DebugTools } from '../systems/DebugTools';
import { ShakeRig } from '../systems/Feel';
import { Flecks, Wake } from '../systems/Flecks';
import { Ghost } from '../systems/Ghost';
import { Hud } from '../systems/Hud';
import { createSeededRandom } from '../utils/random';
import { resolveDuckBumps } from './Pilot';
import { GATE_D, RIVER_LENGTH, River, TUNNEL_RADIUS } from './River';
import { Profile, raceCoins } from './Profile';
import { PerfMonitor } from '../systems/PerfMonitor';
import { Shop } from '../systems/Shop';
import {
  CLOSE_SHAVE_GAP,
  COUNTDOWN_SECONDS,
  CRUMB_STREAK,
  CRUMB_STREAK_GAP,
  DEFAULT_TUNING,
  DUCK_RADIUS,
  FLAP_COOLDOWN,
  medalFor,
  PADDLE_CHARGES,
  type Tuning,
} from './tuning';

type Phase = 'countdown' | 'racing' | 'finished';

/** `?start=90` (debug) begins the run further down the river to practise a section. */
const START_D = (() => {
  const requested = Number(new URLSearchParams(window.location.search).get('start'));
  return Number.isFinite(requested) && requested > 0 ? Math.min(requested, RIVER_LENGTH - 10) : 3;
})();
/** A `?start=` run is practice: it skips part of the river, so it can't set a best or pay coins. */
const PRACTICE = START_D !== 3;
type Coached = { crumb: boolean; item: boolean };
const COACH_KEY = 'quackwater:coached-v1';

/** Which first-time callouts the player has seen. `?tutorial` shows them again. */
function loadCoached(): Coached {
  const fresh: Coached = { crumb: false, item: false };
  try {
    if (new URLSearchParams(location.search).has('tutorial')) {
      localStorage.removeItem(COACH_KEY);
      return fresh;
    }
    return { ...fresh, ...JSON.parse(localStorage.getItem(COACH_KEY) ?? '{}') };
  } catch {
    return fresh;
  }
}

/** Crumb pickup probe used outside a race so nothing gets collected. */
const FAR_AWAY = new THREE.Vector3(0, -999, 0);
const SKY = '#a8dcef';

export class Game {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(55, 1, 0.1, 140);
  private readonly tuning: Tuning = { ...DEFAULT_TUNING };
  private readonly river = new River(this.tuning);
  private readonly riverView: RiverView;
  private readonly profile = new Profile();
  private readonly duck = new Duck(this.profile.skin);
  private readonly ghost = new Ghost();
  private readonly flecks: Flecks;
  private readonly wake = new Wake();
  private readonly crumbs: Crumbs;
  private readonly hazards: Hazards;
  private readonly rivals: Rival[];
  /** Player first, then rivals: the order hazards and bumps expect. */
  private readonly allDucks: Duck[];
  private place = 1;
  private readonly powerUps: PowerUps;
  private lurePopCooldown = 0;
  private readonly hemi = new THREE.HemisphereLight('#e8f6ff', '#4f7a3a', 1.6);
  /** A small warm light that follows the duck inside the pipe. */
  private readonly duckLight = new THREE.PointLight('#ffd98a', 0, 9, 1.5);
  private readonly input: SteerInput;
  private readonly audio = new AudioSystem();
  private readonly hud = new Hud();
  private readonly cameraRig = new CameraRig(this.camera);
  private readonly shake = new ShakeRig();
  private readonly sun = new THREE.DirectionalLight('#fff4d6', 2.4);
  private readonly debugTools: DebugTools;
  private readonly dir = { x: 0, z: -1 };
  private readonly loop = new Loop(
    (delta, elapsed) => this.update(delta, elapsed),
    () => this.render(),
  );

  // Route ALL gameplay randomness through this.rng (never Math.random) so the
  // seed() test hook keeps screenshot baselines and bot playtests deterministic.
  private rng = createSeededRandom(7);
  private frame = 0;
  private phase: Phase = 'countdown';
  private countdown = COUNTDOWN_SECONDS;
  private raceTime = 0;
  private finishedAt = 0;
  private clock = 0;
  private goBannerTimer = 0;
  private paddleTimer = 0;
  private bumpCooldown = 0;
  private flapCooldown = 0;
  /** Steering input read this frame, for diagnostics. */
  private lastSteer = 0;
  /** First-time teaching moments already shown (kept across races). */
  private readonly coached = loadCoached();
  private readonly screenPos = new THREE.Vector3();
  private paddles = PADDLE_CHARGES;
  private crumbStreak = 0;
  /** Slow-motion while airborne off a waterfall; eases back to 1. */
  private timeScale = 1;
  private airLift = 0;
  private lastCrumbAt = -10;
  private fovKick = 0;
  private inFastWater = false;
  /** Closest surface gap to each rock this run; Infinity = not near yet, -1 = hit it. */
  private rockGaps: number[] = [];
  private rockResolved: boolean[] = [];
  private paused = false;
  /** On the title card: the duck waits at the line until the player starts. */
  private onTitle = true;
  private readonly shop: Shop;
  /** Phone testing: `?debug`/`?perf` shows a frame readout; on the dev server over the LAN it also reports to the Mac. */
  private readonly perf: PerfMonitor;
  private frameStart = 0;
  private pausedForScreenshot = false;
  private reducedMotion = false;

  constructor(private readonly canvas: HTMLCanvasElement) {
    this.renderer = createRenderer(canvas);
    this.renderer.toneMappingExposure = this.tuning.exposure;
    this.riverView = new RiverView(this.river, createSeededRandom(21));
    this.flecks = new Flecks(this.river, this.rng);
    this.crumbs = new Crumbs(this.river);
    this.hazards = new Hazards(this.river, createSeededRandom(5));
    this.rivals = RIVAL_PROFILES.map((profile, i) => new Rival(profile, createSeededRandom(31 + i)));
    this.allDucks = [this.duck, ...this.rivals.map((rival) => rival.duck)];
    this.powerUps = new PowerUps(this.river, createSeededRandom(77), this.allDucks.length);
    this.hud.setRivals(this.rivals.map((rival) => rival.profile.body));

    this.input = new SteerInput(canvas, this.getElement('#paddle-button'), (kind) => this.onAction(kind));
    this.input.onDrag = (drag) => this.hud.setDrag(this.phase === 'racing' ? drag : null);
    this.getElement('#pause-button').addEventListener('click', this.onPauseClick);
    this.getElement('#title-screen').addEventListener('pointerdown', this.onTitlePress);
    this.shop = new Shop(
      this.profile,
      (skin) => {
        this.duck.restyle(skin);
        this.hud.setCoins(this.profile.coins);
      },
      () => this.hud.setCoins(this.profile.coins),
    );
    const shopButton = this.getElement('#shop-button');
    shopButton.addEventListener('pointerdown', (event) => event.stopPropagation());
    shopButton.addEventListener('click', () => this.shop.open());
    this.hud.setCoins(this.profile.coins);
    this.getElement('#resume-button').addEventListener('click', this.onPauseClick);
    this.getElement('#restart-button').addEventListener('click', this.onRestartClick);
    this.getElement('#item-button').addEventListener('pointerdown', this.onItemPress);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.debugTools = new DebugTools(
      this.tuning,
      () => this.riverView.bakeFlow(),
      () => {
        this.renderer.toneMappingExposure = this.tuning.exposure;
        resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
      },
    );

    const params = new URLSearchParams(window.location.search);
    const remote = !['localhost', '127.0.0.1'].includes(window.location.hostname);
    this.perf = new PerfMonitor(this.renderer, {
      overlay: params.has('debug') || params.has('perf'),
      report: import.meta.env.DEV && (remote || params.has('perf')),
    });

    this.createScene();
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    this.resetRun();
    this.installTestHooks();
    this.publishDiagnostics();
  }

  private readonly onPauseClick = () => this.setPaused(!this.paused);
  private readonly onTitlePress = (event: PointerEvent) => {
    event.preventDefault();
    if (this.shop.isOpen) return;
    this.leaveTitle();
  };

  private leaveTitle(): void {
    if (!this.onTitle) return;
    this.onTitle = false;
    this.hud.showTitle(false);
    this.resetRun();
  }
  private readonly onItemPress = (event: PointerEvent) => {
    event.preventDefault();
    this.onAction('item');
  };
  private readonly onRestartClick = () => {
    this.setPaused(false);
    this.resetRun();
  };
  private readonly onVisibility = () => {
    if (document.hidden && this.phase !== 'finished') this.setPaused(true);
  };

  private setPaused(paused: boolean): void {
    if (paused && (this.phase === 'finished' || this.onTitle)) return;
    this.paused = paused;
    this.hud.showPaused(paused);
    this.audio.setPaused(paused);
  }

  start(): void {
    this.loop.start();
  }

  dispose(): void {
    this.loop.stop();
    this.input.dispose();
    this.perf.dispose();
    this.getElement('#pause-button').removeEventListener('click', this.onPauseClick);
    this.getElement('#title-screen').removeEventListener('pointerdown', this.onTitlePress);
    this.getElement('#resume-button').removeEventListener('click', this.onPauseClick);
    this.getElement('#restart-button').removeEventListener('click', this.onRestartClick);
    this.getElement('#item-button').removeEventListener('pointerdown', this.onItemPress);
    this.powerUps.dispose();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.hazards.dispose();
    this.audio.dispose();
    this.debugTools.dispose();
    this.duck.dispose();
    for (const rival of this.rivals) rival.dispose();
    this.ghost.dispose();
    this.flecks.dispose();
    this.wake.dispose();
    this.riverView.dispose();
    this.renderer.dispose();
    window.__THREE_GAME_DIAGNOSTICS__ = undefined;
    window.__THREE_GAME_TEST_HOOKS__ = undefined;
  }

  private createScene(): void {
    this.scene.background = new THREE.Color(SKY);
    this.scene.fog = new THREE.Fog(SKY, 38, 95);

    this.scene.add(this.hemi, this.duckLight);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(1024, 1024);
    const cam = this.sun.shadow.camera;
    cam.near = 1;
    cam.far = 60;
    cam.left = -22;
    cam.right = 22;
    cam.top = 22;
    cam.bottom = -22;
    this.scene.add(this.sun, this.sun.target);

    this.scene.add(
      this.riverView.group,
      this.flecks.group,
      this.wake.mesh,
      this.crumbs.mesh,
      this.hazards.group,
      this.powerUps.group,
      this.ghost.group,
      ...this.rivals.map((rival) => rival.duck.group),
      this.duck.group,
    );
  }

  private resetRun(): void {
    this.phase = 'countdown';
    this.countdown = COUNTDOWN_SECONDS;
    this.raceTime = 0;
    this.goBannerTimer = 0;
    this.bumpCooldown = 0;
    this.flapCooldown = 0;
    this.paddles = PADDLE_CHARGES;
    this.fovKick = 0;
    this.inFastWater = false;
    this.rockGaps = this.river.rocks.map(() => Infinity);
    this.rockResolved = this.river.rocks.map(() => false);
    this.crumbStreak = 0;
    this.lastCrumbAt = -10;
    this.crumbs.reset();
    this.hazards.reset();
    this.powerUps.reset();
    this.hud.setItem(null);
    this.hud.hideCoach();
    this.hud.setPaddles(this.paddles);
    // Line up four abreast; the player second from the left.
    const grid = [-0.22, 0.22, -0.66, 0.66];
    const lineUp = (s: number) => {
      const x = this.river.xAt(START_D, s);
      return [x, this.river.heightAt(START_D, x), -START_D] as const;
    };
    this.duck.reset(...lineUp(grid[0]));
    this.rivals.forEach((rival, i) => rival.reset(...lineUp(grid[i + 1])));
    this.place = 1;
    this.flecks.reset(START_D);
    this.wake.reset();
    this.ghost.startRun();
    this.shake.reset();
    const dir = this.river.direction(START_D, this.dir);
    this.cameraRig.snapTo(this.duck.position, dir.x, dir.z);
    this.hud.hideResults();
    this.hud.showHint(!this.onTitle);
    // On the title card the countdown waits; it starts when the player does.
    this.hud.showBanner(this.onTitle ? '' : String(COUNTDOWN_SECONDS));
    if (!this.onTitle) this.audio.beep(false);
  }

  private startRacing(): void {
    this.phase = 'racing';
    this.countdown = 0;
    this.goBannerTimer = 0.7;
    this.hud.showBanner('GO!');
    this.audio.beep(true);
  }

  private finishRace(): void {
    this.phase = 'finished';
    this.finishedAt = this.clock;
    const previousBest = this.ghost.bestTime;
    const isBest = PRACTICE ? false : this.ghost.finishRun(this.raceTime);
    // Rivals still on the water finish behind you.
    this.place = 1 + this.rivals.filter((rival) => rival.finishTime !== null && rival.finishTime < this.raceTime).length;
    this.hud.showBanner('');
    // Pay out: place, crumbs and medal, banked straight away.
    const lines = PRACTICE ? [] : raceCoins(this.place, this.crumbs.collected, medalFor(this.raceTime));
    this.profile.addCoins(lines.reduce((sum, line) => sum + line.coins, 0));
    this.hud.setCoins(this.profile.coins);
    this.hud.showResults(
      this.raceTime,
      previousBest,
      isBest,
      this.crumbs.collected,
      this.crumbs.total,
      this.place,
      this.allDucks.length,
      PRACTICE ? null : { lines, total: this.profile.coins },
      PRACTICE,
    );
    this.audio.finish(isBest);
  }

  private onAction(kind: InputAction): void {
    // The shop takes over input: Esc closes it, nothing else starts a race.
    if (this.shop.isOpen) {
      if (kind === 'pause') this.shop.close();
      return;
    }
    if (kind === 'pause') {
      this.setPaused(!this.paused);
      return;
    }
    if (this.paused) return;
    if (this.onTitle) {
      this.leaveTitle();
      return;
    }
    if (kind === 'restart') this.resetRun();
    else if (this.phase === 'finished') {
      if (this.clock - this.finishedAt > 0.8) this.resetRun();
    } else if (kind === 'paddle') this.paddle();
    else if (kind === 'item') this.useItem();
  }

  private useItem(): void {
    if (this.phase !== 'racing') return;
    const used = this.powerUps.use(0, this.allDucks, this.river, this.tuning);
    if (!used) return;
    this.hud.setItem(null);
    if (this.hud.coachingItem) this.hud.hideCoach();
    if (used === 'boost') {
      this.audio.squeak(2.5);
      this.audio.whoosh();
      this.fovKick = Math.max(this.fovKick, 11);
      this.shake.addTrauma(0.2);
      this.wake.splash(this.duck.position.x, this.duck.position.z, 3);
      this.hud.pop('⚡ Squeaky boost!', 'surf');
    } else if (used === 'shield') {
      this.audio.chime();
      this.hud.pop('🫧 Bubble shield!', 'surf');
    } else {
      this.audio.stroke(0.3);
      this.hud.pop('🍞 Bread thrown!', 'shave');
    }
  }

  private paddle(): void {
    if (this.phase !== 'racing') return;
    if (this.duck.canFlap(this.river, this.tuning)) {
      // Stalled or crawling: a free flap, never a wasted charge.
      if (this.flapCooldown > 0) return;
      this.flapCooldown = FLAP_COOLDOWN;
      const kick = this.duck.flap(this.river, this.tuning);
      if (kick <= 0) return;
      this.audio.stroke(0);
      this.wake.splash(this.duck.position.x, this.duck.position.z, 0.6);
      this.fovKick = Math.max(this.fovKick, 1.5);
      return;
    }
    if (this.paddles < 1) {
      this.audio.dud();
      return;
    }
    this.paddles -= 1;
    // How fast is the water under the duck? Paddling in fast water surfs it.
    const surf = Math.max(0, Math.min(1, (this.duck.flow.speed / this.river.maxSpeed() - 0.45) / 0.5));
    const waveJump = this.duck.paddle(this.tuning.paddleKick + this.tuning.paddleFastBonus * surf, this.river, this.tuning);
    if (waveJump) this.hud.pop('Wave jump!', 'surf');
    this.audio.stroke(surf);
    this.wake.splash(this.duck.position.x, this.duck.position.z, 1 + surf * 3);
    this.fovKick = Math.max(this.fovKick, 3 + surf * 6);
    this.shake.addTrauma(0.08 + surf * 0.12);
    if (surf > 0.7) this.hud.pop('SURF!', 'surf');
  }

  private markCoached(moment: keyof Coached): void {
    this.coached[moment] = true;
    try {
      localStorage.setItem(COACH_KEY, JSON.stringify(this.coached));
    } catch {
      // Storage blocked: the callouts just show again next visit.
    }
  }

  private refundPaddle(): void {
    this.paddles = Math.min(PADDLE_CHARGES, Math.floor(this.paddles) + 1);
    this.hud.flashPaddles();
  }

  private updateCrumbs(time: number): void {
    const got = this.crumbs.update(time, this.duck.position);
    if (got > 0) {
      // Show the crumb flying into the paddle bar: crumbs are what fill it.
      this.screenPos.copy(this.duck.position).project(this.camera);
      // Teach it the first time a crumb visibly fills a charge (not on a full bar).
      const first = !this.coached.crumb && this.paddles < PADDLE_CHARGES;
      this.hud.crumbFly((this.screenPos.x + 1) / 2, (1 - this.screenPos.y) / 2, this.paddles, first);
      if (first) {
        this.markCoached('crumb');
        this.hud.coach('paddle', '🍞 Crumbs charge your paddles<small>and give you a little push</small>');
      }
    }
    for (let i = 0; i < got; i += 1) {
      this.crumbStreak = this.raceTime - this.lastCrumbAt < CRUMB_STREAK_GAP ? this.crumbStreak + 1 : 1;
      this.lastCrumbAt = this.raceTime;
      // Riding the crumb line is how you keep your paddles topped up, and a nibble of speed.
      this.paddles = Math.min(PADDLE_CHARGES, this.paddles + this.tuning.crumbCharge);
      this.duck.nibble(this.tuning.crumbKick, this.river);
      this.audio.crumb(this.crumbStreak);
      if (this.crumbStreak % CRUMB_STREAK === 0) {
        this.refundPaddle();
        this.hud.pop(`${this.crumbStreak} crumb streak! +1`, 'shave');
      }
    }
  }

  private updateCloseShaves(): void {
    const d = this.duck.distance;
    this.river.rocks.forEach((rock, i) => {
      if (this.rockResolved[i]) return;
      if (Math.abs(rock.d - d) < rock.r + 2.5) {
        const gap = Math.hypot(this.duck.position.x - rock.x, this.duck.position.z - rock.z) - rock.r - DUCK_RADIUS;
        if (gap < 0.02) this.rockGaps[i] = -1;
        else if (this.rockGaps[i] !== -1) this.rockGaps[i] = Math.min(this.rockGaps[i], gap);
      }
      if (d > rock.d + rock.r + 1) {
        this.rockResolved[i] = true;
        if (this.rockGaps[i] >= 0 && this.rockGaps[i] < CLOSE_SHAVE_GAP) {
          this.refundPaddle();
          this.hud.pop('Close shave! +1', 'shave');
          this.audio.chime();
        }
      }
    });
  }

  private update(delta: number, elapsed: number): void {
    this.frame += 1;
    this.frameStart = performance.now();
    resizeRenderer(this.renderer, this.camera, this.tuning.maxDpr);
    if (this.pausedForScreenshot || this.paused) {
      this.publishDiagnostics();
      return;
    }
    const realDelta = delta;
    const targetScale = this.duck.bigAir && !this.reducedMotion ? 0.55 : 1;
    this.timeScale += (targetScale - this.timeScale) * (1 - Math.exp(-12 * realDelta));
    delta *= this.timeScale;
    this.clock += delta;

    if (this.phase === 'countdown' && !this.onTitle) {
      const before = Math.ceil(this.countdown);
      this.countdown -= delta;
      const after = Math.ceil(this.countdown);
      if (this.countdown <= 0) this.startRacing();
      else if (after !== before) {
        this.hud.showBanner(String(after));
        this.audio.beep(false);
      }
    } else if (this.goBannerTimer > 0) {
      this.goBannerTimer -= delta;
      if (this.goBannerTimer <= 0) this.hud.showBanner('');
    }

    const racing = this.phase === 'racing';
    const steer = racing ? this.input.readSteer() : 0;
    this.lastSteer = steer;
    const wasBigAir = this.duck.bigAir;
    this.duck.update(delta, steer, this.phase !== 'countdown', this.river, this.tuning);
    const live = this.phase !== 'countdown';
    const logs = this.hazards.logObstacles();
    this.rivals.forEach((rival, i) => {
      const lure = this.powerUps.lureFor(i + 1, rival.duck);
      rival.update(delta, live, live, this.raceTime, this.duck.distance, this.river, this.tuning, lure, logs);
      const item = this.powerUps.held[i + 1];
      if (live && item && rival.wantsToUse(item, this.duck, this.river, delta)) {
        this.powerUps.use(i + 1, this.allDucks, this.river, this.tuning);
        rival.usedItem();
      }
    });
    if (live) this.updatePowerUps(delta);
    this.hazards.update(delta, this.raceTime, this.allDucks, racing);
    const bump = live ? resolveDuckBumps(this.allDucks) : 0;
    if (bump > 1.2 && this.bumpCooldown <= 0) {
      this.bumpCooldown = 0.3;
      this.audio.squeak(bump * 0.6);
      this.duck.bump(Math.min(0.6, bump * 0.15));
      this.shake.addTrauma(Math.min(0.3, bump * 0.06));
    }
    if (racing) this.updatePlace();
    this.handleHazardEvents();
    if (racing) {
      this.raceTime += delta;
      this.paddles = Math.min(PADDLE_CHARGES, this.paddles + delta / this.tuning.paddleRecharge);
      this.updateCloseShaves();
      this.ghost.record(delta, this.duck.position.x, this.duck.position.y, this.duck.position.z, this.duck.heading);
      if (this.raceTime > 4) this.hud.showHint(false);
      if (this.duck.distance >= RIVER_LENGTH) this.finishRace();
    }
    this.handleDuckEvents(delta, steer, wasBigAir);
    if (racing) this.updateCrumbs(this.clock);
    else this.crumbs.update(this.clock, FAR_AWAY);

    const animDelta = this.reducedMotion ? 0 : delta;
    const animTime = this.reducedMotion ? 0 : elapsed;
    const speed01 = Math.hypot(this.duck.velocity.x, this.duck.velocity.z) / this.river.maxSpeed();
    this.flecks.update(animDelta, this.duck.distance);
    this.wake.setWaterY(this.river.heightAt(this.duck.distance, this.duck.position.x));
    // Ripples come from moving through the water, not with it.
    const relX = this.duck.velocity.x - this.duck.flow.x;
    const relZ = this.duck.velocity.z - this.duck.flow.z;
    const push = this.phase === 'countdown' || this.duck.inAir ? 0 : Math.min(1, Math.hypot(relX, relZ) / 2.5);
    this.wake.update(
      animDelta,
      this.duck.position.x,
      this.duck.position.z,
      relX,
      relZ,
      this.duck.flow.x,
      this.duck.flow.z,
      push,
    );
    this.riverView.update(animDelta, animTime);

    const ghostPos = this.ghost.update(this.phase === 'countdown' ? 0 : this.raceTime);
    this.audio.setRiver(this.phase === 'countdown' ? 0.1 : speed01);

    // Catching fast water should feel like something.
    const water01 = this.duck.flow.speed / this.river.maxSpeed();
    if (racing && !this.inFastWater && water01 > 0.8) {
      this.inFastWater = true;
      this.audio.whoosh();
      this.fovKick = Math.max(this.fovKick, 4);
    } else if (this.inFastWater && water01 < 0.62) {
      this.inFastWater = false;
    }
    this.fovKick *= Math.exp(-3 * delta);
    const extraFov = this.reducedMotion ? 0 : Math.min(1.4, speed01) * 7 + this.fovKick;

    // Rise while flying so the drop below reads as a drop.
    // Rise over the sluice frame so it never hides the duck as the camera passes it.
    const gateBehind = this.duck.distance > GATE_D - 2 && this.duck.distance < GATE_D + 9;
    const liftTarget = (this.duck.bigAir ? 1.6 : 0) + (gateBehind ? 2.6 : 0);
    this.airLift += (liftTarget - this.airLift) * (1 - Math.exp(-5 * realDelta));

    // The pipe: dim the daylight, light the duck, echo the sound, drop the camera in.
    const inside = this.river.tunnelAt(this.duck.distance);
    this.hemi.intensity = 1.6 * (1 - inside * 0.7);
    this.sun.intensity = 2.4 * (1 - inside * 0.85);
    this.duckLight.intensity = inside * 5;
    this.duckLight.position.set(this.duck.position.x, this.duck.position.y + 1.4, this.duck.position.z + 0.8);
    this.audio.setEcho(inside);
    const cameraInside = this.river.tunnelAt(this.duck.distance - 3.5);

    const dir = this.river.direction(this.duck.distance, this.dir);
    this.cameraRig.update(
      realDelta,
      this.duck.position,
      dir.x,
      dir.z,
      this.tuning.cameraLag,
      extraFov * (1 - cameraInside * 0.6),
      this.airLift,
      cameraInside,
    );
    // Keep the camera inside the pipe's bore so it never clips into the walls.
    const camD = -this.camera.position.z;
    const bore = this.river.tunnelAt(camD);
    if (bore > 0.01) {
      const axis = this.river.centerX(camD);
      const room = TUNNEL_RADIUS - 0.7;
      const clampedX = Math.max(axis - room, Math.min(axis + room, this.camera.position.x));
      this.camera.position.x += (clampedX - this.camera.position.x) * bore;
      const ceiling = this.river.heightAt(camD) + TUNNEL_RADIUS - 0.8;
      if (this.camera.position.y > ceiling) this.camera.position.y += (ceiling - this.camera.position.y) * bore;
    }
    if (!this.reducedMotion) this.shake.update(realDelta, this.camera);
    this.sun.position.set(this.duck.position.x - 8, this.duck.position.y + 18, this.duck.position.z + 6);
    this.sun.target.position.set(this.duck.position.x, this.duck.position.y, this.duck.position.z - 6);

    this.hud.setFlap(this.phase === 'racing' && this.duck.canFlap(this.river, this.tuning));
    this.hud.setStreak(this.raceTime - this.lastCrumbAt < CRUMB_STREAK_GAP ? this.crumbStreak % CRUMB_STREAK : 0);
    this.hud.setPaddles(this.paddles);
    this.hud.update(
      this.raceTime,
      this.ghost.bestTime,
      this.duck.distance / RIVER_LENGTH,
      ghostPos ? -ghostPos.z / RIVER_LENGTH : null,
      this.rivals.map((rival) => rival.distance / RIVER_LENGTH),
      this.place,
      this.allDucks.length,
    );
    this.publishDiagnostics();
  }

  private handleHazardEvents(): void {
    const { hit, heronWindup, gateMoved } = this.hazards.events;
    if (heronWindup) this.audio.squawk();
    if (gateMoved && Math.abs(this.duck.distance - GATE_D) < 25) this.audio.clunk(gateMoved === 'open');
    if (hit === 'snap') {
      this.hud.pop('Snap!', 'shave');
      this.audio.squeak(3);
      this.shake.addTrauma(0.45);
      this.wake.splash(this.duck.position.x, this.duck.position.z, 3);
    } else if (hit === 'gate') {
      this.hud.pop('Wait for green!', 'shave');
    }
  }

  private updatePowerUps(delta: number): void {
    this.powerUps.update(delta, this.clock, this.allDucks);
    const { picked, lured } = this.powerUps.events;
    if (picked && this.phase === 'racing') {
      this.audio.crumb(8);
      this.hud.pop(`${ITEM_ICON[picked]} ${ITEM_NAME[picked]}!`, 'surf');
      // The item flies into its button; the first time, a callout says how to use it.
      const first = !this.coached.item;
      const at = this.powerUps.events.pickedAt;
      this.screenPos.set(at.x, at.y, at.z).project(this.camera);
      this.hud.itemFly((this.screenPos.x + 1) / 2, (1 - this.screenPos.y) / 2, ITEM_ICON[picked], first, () => {
        if (this.powerUps.held[0] !== picked) return;
        this.hud.setItem(ITEM_ICON[picked]);
        if (first) {
          this.hud.coach(
            'item',
            `${ITEM_ICON[picked]} ${ITEM_NAME[picked]}!<br><span class="coach-keys">Press <kbd>E</kbd> to use it</span><span class="coach-touch">Tap the button to use it</span>`,
          );
        }
      });
      if (first) this.markCoached('item');
    }
    this.lurePopCooldown -= delta;
    if (lured && this.lurePopCooldown <= 0) {
      this.lurePopCooldown = 3;
      this.hud.pop('🍞 Lured!', 'shave');
    }
    if (this.duck.events.shieldPopped) {
      this.audio.chime();
      this.hud.pop('🫧 Pop! Shield saved you', 'surf');
    }
  }

  /** Live standing: finished rivals are ahead, the rest by distance. */
  private updatePlace(): void {
    const d = this.duck.distance;
    this.place = 1 + this.rivals.filter((rival) => rival.finishTime !== null || rival.distance > d).length;
  }

  private handleDuckEvents(delta: number, steer: number, wasBigAir: boolean): void {
    this.bumpCooldown -= delta;
    this.flapCooldown -= delta;
    const { rockHit, bankHit, ejected, launched, landed, spunOut } = this.duck.events;
    if (launched) {
      this.audio.whoosh();
      this.fovKick = Math.max(this.fovKick, 6);
    }
    if (landed > 0 && !wasBigAir) {
      // A little wave hop: just a splash.
      this.wake.splash(this.duck.position.x, this.duck.position.z, 1);
      this.audio.stroke(0.1);
    } else if (landed > 0) {
      const water01 = this.duck.flow.speed / this.river.maxSpeed();
      this.wake.splash(this.duck.position.x, this.duck.position.z, 3 + landed * 0.4);
      this.shake.addTrauma(Math.min(0.5, 0.15 + landed * 0.04));
      this.audio.thud(0.2);
      if (water01 > 0.72 && !this.duck.stunned) {
        // Landing on the fast line is the skill test of every drop.
        this.duck.paddle(this.tuning.paddleKick + this.tuning.paddleFastBonus, this.river, this.tuning);
        this.refundPaddle();
        this.audio.stroke(1);
        this.fovKick = Math.max(this.fovKick, 8);
        this.hud.pop('Perfect landing! +1', 'surf');
      } else {
        this.audio.stroke(0.2);
        this.hud.pop('Splash!', 'surf');
      }
    }
    if (spunOut && !this.hazards.events.hit && !this.duck.events.shieldPopped) {
      this.hud.pop('Bonk!', 'shave');
      this.shake.addTrauma(0.35);
    }
    if (ejected) {
      this.audio.squeak(1);
      this.duck.bump(0.6);
      this.wake.splash(this.duck.position.x, this.duck.position.z, 4);
    }
    if (rockHit > 0.5 && this.bumpCooldown <= 0) {
      this.bumpCooldown = 0.25;
      this.audio.squeak(rockHit);
      this.duck.bump(Math.min(1, rockHit * 0.25));
      this.shake.addTrauma(Math.min(0.6, 0.2 + rockHit * 0.08));
      this.wake.splash(this.duck.position.x, this.duck.position.z, rockHit);
    } else if (bankHit > 0.8 && this.bumpCooldown <= 0) {
      this.bumpCooldown = 0.25;
      this.audio.thud(0.08 + bankHit * 0.02);
      this.duck.bump(0.2);
    }

    if (steer !== 0) {
      this.paddleTimer -= delta;
      if (this.paddleTimer <= 0) {
        this.paddleTimer = 0.2;
        this.audio.paddle();
      }
    } else {
      this.paddleTimer = 0;
    }
  }

  private render(): void {
    this.renderer.render(this.scene, this.camera);
    if (this.perf.active) {
      this.perf.frame(performance.now() - this.frameStart, this.duck.distance, this.phase === 'racing', {
        maxDpr: this.tuning.maxDpr,
        phase: this.phase,
      });
    }
  }

  private installTestHooks(): void {
    // Deterministic hooks consumed by tests/visual-regression.template.ts and
    // bot playtests. Keep these real when evolving the game: silent no-op hooks
    // produce flaky screenshot baselines.
    window.__THREE_GAME_TEST_HOOKS__ = {
      seed: (value: number) => {
        this.rng = createSeededRandom(value);
      },
      setState: (name: string) => {
        if (name !== 'countdown' && name !== 'active-play' && name !== 'complete') {
          throw new Error(`Unknown test state: ${name}`);
        }
        this.onTitle = false;
        this.hud.showTitle(false);
        this.resetRun();
        if (name === 'active-play' || name === 'complete') this.startRacing();
        if (name === 'complete') {
          this.duck.reset(this.river.laneX(RIVER_LENGTH), this.river.heightAt(RIVER_LENGTH), -RIVER_LENGTH);
          const end = this.river.direction(RIVER_LENGTH, this.dir);
          this.cameraRig.snapTo(this.duck.position, end.x, end.z);
          this.place = 2;
          this.phase = 'finished';
          this.raceTime = 42.5;
          this.hud.showBanner('');
          this.hud.showHint(false);
          this.hud.showResults(this.raceTime, null, false, 40, this.crumbs.total, 2, this.allDucks.length);
        }
        this.render();
        this.publishDiagnostics();
        return { state: name };
      },
      setPausedForScreenshot: (paused: boolean) => {
        this.pausedForScreenshot = paused;
      },
      setReducedMotion: (enabled: boolean) => {
        this.reducedMotion = enabled;
        if (enabled) this.duck.stabilizeVisuals();
        this.render();
        this.publishDiagnostics();
      },
      hideDebugUi: (hidden: boolean) => {
        this.debugTools.setHidden(hidden);
      },
    };
  }

  private getElement(selector: string): HTMLElement {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) throw new Error(`Missing element: ${selector}`);
    return element;
  }

  private publishDiagnostics(): void {
    const info = this.renderer.info;
    window.__THREE_GAME_DIAGNOSTICS__ = {
      frame: this.frame,
      phase: this.phase,
      raceTime: this.raceTime,
      progress: this.duck.distance / RIVER_LENGTH,
      place: this.place,
      rivals: this.rivals.map((rival) => ({ progress: rival.distance / RIVER_LENGTH, finishTime: rival.finishTime })),
      bestTime: this.ghost.bestTime,
      paddles: this.paddles,
      steer: this.lastSteer,
      audio: this.audio.state,
      item: this.powerUps.held[0],
      paused: this.paused,
      gateOpen: this.hazards.gateIsOpen(),
      crumbs: this.crumbs.collected,
      airborne: this.duck.inAir,
      player: {
        position: { x: this.duck.position.x, y: this.duck.position.y, z: this.duck.position.z },
        speed: Math.hypot(this.duck.velocity.x, this.duck.velocity.z),
        waterSpeed: this.duck.flow.speed,
        inWhirlpool: this.duck.flow.whirl > 0,
        laneOffset: this.duck.position.x - this.river.laneX(this.duck.distance),
      },
      renderer: {
        calls: info.render.calls,
        triangles: info.render.triangles,
        geometries: info.memory.geometries,
        textures: info.memory.textures,
      },
      canvas: {
        clientWidth: this.canvas.clientWidth,
        clientHeight: this.canvas.clientHeight,
        width: this.canvas.width,
        height: this.canvas.height,
        dpr: Math.min(window.devicePixelRatio || 1, this.tuning.maxDpr),
      },
    };
  }
}
